import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  Grid,
  Button,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Autocomplete,
} from "@mui/material";
import { useSelector } from "react-redux";
import MicIcon from "@mui/icons-material/Mic";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";

import axios from "axios";
import ReusableDialog from "../../../../common/dialog/dialog";
import SelectableRadioGroup from "../../../../common/selectableRadioGroup/selectable-radio-group";

const strategies = [
  "Details of debrief ",
  "Could this incident have been avoided ? ",
  "Concerns raised",
  "Lessons learnt",
  "Support offered",
  "Actions to prevent immediate reoccurrence & allocated to",
];

const StaffMember = ({ updateFormData, prevStep, formData, person }) => {
  const user = useSelector((state) => state.auth.user); // User data from Redux store
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState("");
  const [recording, setRecording] = useState(false);
  const [audioURL, setAudioURL] = useState(null);
  const [note, setNote] = useState("");
  const mediaRecorderRef = useRef(null);
  const audioChunks = useRef([]);
  const [incidentRecords, setIncidentRecords] = useState([]); // Stores submitted voice records
  const [present, setPresent] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(person || ""); // Store selected user object

  const allowedBehaviors = Array.isArray(incidentRecords) ? incidentRecords.map((b) => b.behavior) : [];

  useEffect(() => {
    if (formData && formData.length > 0) {
      setIncidentRecords(formData);
    }
  }, [formData]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // Make the API request
        const response = await axios.get("/api/auth/users");
        // Filter users with roleid === 4 and update state
        const filteredUsers = response.data.filter((usera) => usera.id !== user?.id);
        setUsers(filteredUsers);
      } catch (error) {
        console.error("Error fetching tasks:", error);
      }
    };

    fetchUsers();
  }, [user?.id]);

  const handleVerbalStatus = (event, newStatus) => {
    setPresent(newStatus);
  };

  // Start Recording
  const startRecording = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mediaRecorder = new MediaRecorder(stream);
    mediaRecorderRef.current = mediaRecorder;
    audioChunks.current = [];

    mediaRecorder.ondataavailable = (event) => {
      audioChunks.current.push(event.data);
    };

    mediaRecorder.onstop = () => {
      const audioBlob = new Blob(audioChunks.current, { type: "audio/wav" });
      const audioUrl = URL.createObjectURL(audioBlob);
      setAudioURL(audioUrl);
    };

    mediaRecorder.start();
    setRecording(true);
  };

  // Stop Recording
  const stopRecording = () => {
    mediaRecorderRef.current.stop();
    setRecording(false);
  };

  // Handle Submit - Save Recorded Entry
  const handleRecordSubmit = async () => {
    // Prevent submission if both note and audio are empty
    if (!note.trim() && !audioURL) {
      alert("Please provide a note or an audio recording.");
      return;
    }

    // Check for duplicate behavior entry
    const isDuplicate = incidentRecords.some((record) => record.behavior === selectedStrategy);
    if (isDuplicate) {
      alert("This behavior has already been recorded.");
      return;
    }

    try {
      let audioUrl = null;

      // Upload audio if it exists
      if (audioURL) {
        const response = await fetch(audioURL);
        const blob = await response.blob();

        const formData = new FormData();
        formData.append("audio", blob, "recording.wav");

        const uploadRes = await fetch("/api/audio", {
          method: "POST",
          body: formData,
        });

        const result = await uploadRes.json();
        audioUrl = result.audioUrl;
      }

      const newRecord = {
        behavior: selectedStrategy,
        audio: audioUrl,
        note: note.trim(),
        present: present ?? null,
      };

      const updatedRecords = [...incidentRecords, newRecord];
      setIncidentRecords(updatedRecords);
      updateFormData({ staffMemberInfo: updatedRecords });

      // Reset inputs
      setDialogOpen(false);
      setAudioURL(null);
      setNote("");
      setSelectedStrategy("");
    } catch (err) {
      console.error("Upload or submission failed", err);
    }
  };

  const handleClose = () => {
    setDialogOpen(false);
    setNote("");
    setAudioURL(null);
    setSelectedStrategy(""); // Reset criteria after submission
  };

  // Handle Deleting a Record
  const handleDeleteRecord = (index) => {
    const updatedRecords = incidentRecords.filter((_, i) => i !== index);
    setIncidentRecords(updatedRecords);
  };

  return (
    <Box sx={{ pl: 4, pr: 4, ml: 2 }}>
      <Chip
        variant='contained'
        sx={{
          fontSize: "14px",
          backgroundColor: "#F2F6FF",
          color: "blue",
        }}
        label='Daily Log'
        size='small'
      />
      <Typography sx={{ fontSize: "24px", color: "black", fontWeight: "bold" }}>Staff Debrief</Typography>
      <Typography variant='subtitle2' sx={{ color: "#757575" }}>
        Date: {new Date().toLocaleDateString()} &nbsp; • &nbsp;{" "}
        <span style={{ color: "#1751D0" }}>{user.username}</span>
      </Typography>
      <Box sx={{ borderBottom: "1px solid #E0E0E0", mt: 2, mb: 2 }} />

      <Grid container spacing={2}>
        <Grid item lg={8}>
          <Typography sx={{ fontSize: "16px", fontWeight: "bold", color: "#333", mb: 2 }}>Staff Debrief</Typography>
          <Grid container spacing={2} mb={3}>
            <Grid item lg={6}>
              <Autocomplete
                id='name'
                options={users} // Use the full users array (not just names)
                getOptionLabel={(option) => option?.username || ""} // ✅ Display username
                value={users.find((u) => u.username === selectedUser) || null} // ✅ Maintain object for proper matching
                onChange={(event, newValue) => {
                  setSelectedUser(newValue); // Store the selected user object
                  updateFormData({ selectedStaff: newValue.username });
                }}
                renderInput={(params) => <TextField {...params} placeholder='Select the staff member' />}
                fullWidth
              />
            </Grid>
          </Grid>
          <SelectableRadioGroup
            options={strategies}
            selectedValue={selectedStrategy}
            setSelectedValue={setSelectedStrategy}
            setDialogOpen={setDialogOpen}
            allowedOptions={allowedBehaviors}
          />

          <ReusableDialog
            isOpen={dialogOpen}
            onClose={handleClose}
            title={selectedStrategy}
            onSubmit={handleRecordSubmit}
            submitText='Submit'
            cancelText='Cancel'
            disable={!audioURL && !note}
          >
            {/* Description */}

            {selectedStrategy.includes("debrief") && (
              <Typography variant='body2' sx={{ color: "#757575", mb: 2 }}>
                Give brief description about Details of debrief
              </Typography>
            )}

            {selectedStrategy.includes("avoided") && (
              <>
                <Grid container mb={1}>
                  <Grid item xs={12} sm={10}>
                    <Typography sx={{ fontSize: "14px", mb: 1 }}>Provide clear Details</Typography>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <ToggleButtonGroup value={present} exclusive onChange={handleVerbalStatus}>
                      <ToggleButton value='Yes' sx={{ textTransform: "none" }} color='primary' size='small'>
                        Yes
                      </ToggleButton>
                      <ToggleButton value='No' sx={{ textTransform: "none" }} color='error' size='small'>
                        No
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Grid>
                </Grid>
              </>
            )}
            {selectedStrategy.includes("Concerns") && (
              <Typography variant='body2' sx={{ color: "#757575", mb: 2 }}>
                Give brief description about Details of concerns raised
              </Typography>
            )}
            {selectedStrategy.includes("Lessons") && (
              <Typography variant='body2' sx={{ color: "#757575", mb: 2 }}>
                Give brief description about Details of Lessons learnt
              </Typography>
            )}
            {selectedStrategy.includes("Support") && (
              <Typography variant='body2' sx={{ color: "#757575", mb: 2 }}>
                Give brief description about Details of Support offered
              </Typography>
            )}

            {/* Audio Recording UI */}
            <Box
              sx={{
                border: "2px dashed #ccc",
                padding: 3,
                textAlign: "center",
                borderRadius: "8px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 180,
                mb: 2,
              }}
            >
              {!audioURL ? (
                <>
                  <Typography sx={{ color: "#757575", fontSize: "16px", mb: 1 }}>
                    Try Recording Your Sound Now
                  </Typography>
                  <MicIcon sx={{ fontSize: 50, color: "#757575", mb: 1 }} />
                  <Typography sx={{ fontSize: "14px", color: "#757575", mb: 1 }}>
                    To start recording press the <b>“Start Recording”</b> button and start recording!
                  </Typography>
                  <Button variant='contained' color='primary' onClick={recording ? stopRecording : startRecording}>
                    {recording ? "Stop Recording" : "Start Recording"}
                  </Button>
                </>
              ) : (
                <>
                  <audio controls src={audioURL} />
                  <Button variant='contained' color='primary' sx={{ mt: 2 }} onClick={() => setAudioURL(null)}>
                    Record Again
                  </Button>
                </>
              )}
            </Box>

            {/* Notes Field */}
            <Typography sx={{ fontSize: "14px", mb: 1 }}>Comment</Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              placeholder='Enter your note here...'
              variant='outlined'
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </ReusableDialog>
        </Grid>

        {/* Incident Management Details Section */}
        <Grid item lg={4} md={4}>
          {/* Incident Management Details Section - Recorded Entries */}
          <Box sx={{ mt: 4 }}>
            <Typography sx={{ fontSize: "14px", color: "black", fontWeight: "bold", mb: 2 }}>
              Incident Management
            </Typography>
            <Typography sx={{ fontSize: "14px", mb: 3 }}>Behavior Records</Typography>

            {incidentRecords.map((record, index) => (
              <Paper
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: 1,
                  mb: 1,
                  borderRadius: 2,
                  border: "1px solid #ccc",
                }}
              >
                <Typography sx={{ fontSize: "14px", color: "black" }}>
                  {record.audio ? (
                    record.behavior
                  ) : (
                    <>
                      {record.behavior}
                      <br />
                      {record.note}
                    </>
                  )}
                </Typography>

                <Box>
                  {record.audio && (
                    <>
                      <IconButton onClick={() => window.open(record.audio, "_blank")}>
                        <MicIcon color='primary' />
                      </IconButton>
                      <IconButton onClick={() => window.open(record.audio, "_blank")}>
                        <CloudDownloadIcon color='primary' />
                      </IconButton>
                    </>
                  )}
                  <IconButton onClick={() => handleDeleteRecord(index)}>
                    <DeleteIcon color='error' />
                  </IconButton>
                </Box>
              </Paper>
            ))}
          </Box>
        </Grid>
      </Grid>

      {/* Footer Actions */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          position: "sticky",
          bottom: 0,
          background: "#fff",
          padding: 2,
          zIndex: 10,
        }}
      >
        <Button variant='outlined' color='primary' onClick={prevStep} disabled={incidentRecords?.length !== 6}>
          Submit
        </Button>
      </Box>
    </Box>
  );
};

export default StaffMember;
